import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createEndpointGeneratorClient } from './api';
import {
  EndpointAdServerType,
  type EndpointGeneratorRequest,
  type EndpointIntegrationType,
} from './model';

const endpointGeneratorQueryKey = ['supply', 'endpoint-generator'] as const;

function useEndpointGeneratorClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const accessToken = session?.accessToken;
  const client = useMemo(
    () =>
      accessToken
        ? createEndpointGeneratorClient({
            config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
            accessToken,
          })
        : null,
    [accessToken, config.apiBaseUrl, config.networkId],
  );
  if (!client) throw new Error('Shigo session is required.');
  return client;
}

export function useEndpointPublisherOptions(searchTerm: string, selectedId?: string) {
  const client = useEndpointGeneratorClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [...endpointGeneratorQueryKey, 'publishers', normalizedSearch, hydrateId ?? ''],
    queryFn: () => client.listPublishers({ searchTerm: normalizedSearch, selectedId: hydrateId }),
    enabled: Boolean(normalizedSearch || hydrateId),
  });
}

export function useEndpointPlacementOptions(
  searchTerm: string,
  publisherId?: string,
  selectedId?: string,
) {
  const client = useEndpointGeneratorClient();
  const normalizedSearch = searchTerm.trim();
  const hydrateId = normalizedSearch ? undefined : selectedId;
  return useQuery({
    queryKey: [
      ...endpointGeneratorQueryKey,
      'placements',
      publisherId ?? '',
      normalizedSearch,
      hydrateId ?? '',
    ],
    queryFn: () =>
      client.listPlacements({
        searchTerm: normalizedSearch,
        publisherId,
        selectedId: hydrateId,
      }),
    enabled: Boolean(publisherId && (normalizedSearch || hydrateId)),
  });
}

export function useEndpointMacros(adServerType: EndpointAdServerType, enabled: boolean) {
  const client = useEndpointGeneratorClient();
  return useQuery({
    queryKey: [...endpointGeneratorQueryKey, 'macros', adServerType],
    queryFn: () => client.listMacros(adServerType),
    enabled: enabled && adServerType !== EndpointAdServerType.Exchange,
  });
}

export function useEndpointExchangeMacros(enabled: boolean) {
  const client = useEndpointGeneratorClient();
  return useQuery({
    queryKey: [...endpointGeneratorQueryKey, 'exchange-macros'],
    queryFn: () => client.listExchangeMacros(),
    enabled,
  });
}

export function useGenerateExchangeConnectionMutation() {
  const client = useEndpointGeneratorClient();
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- generates endpoint text, writes no cached server state
  return useMutation({
    mutationFn: ({ endpoint, source }: { endpoint: string; source: string }) =>
      client.generateExchangeConnection(endpoint, source),
  });
}

export function useGenerateEndpointMutation() {
  const client = useEndpointGeneratorClient();
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- generates endpoint text, writes no cached server state
  return useMutation({
    mutationFn: ({
      integrationType,
      payload,
    }: {
      integrationType: EndpointIntegrationType;
      payload: EndpointGeneratorRequest;
    }) => client.generate(integrationType, payload),
  });
}
